import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { Dropdown, Input, InputNumber, Select, Tooltip, message } from 'antd';
import { MoreOutlined, PlayCircleOutlined, PlusOutlined, QuestionCircleOutlined, FullscreenOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  IpcButton,
  IpcEmpty,
  IpcFilterToolbar,
  IpcFormField,
  IpcModal,
  IpcRowActions,
  IpcStatusBadge,
  IpcSwitch,
  IpcTable,
  IpcTag,
  ipcConfirm,
} from '../components/ui';
import { nodeAutoCompleteRuleApi } from '../api/services';
import {
  buildNodeAutoCompleteRuleSaveBody,
  mapNodeAutoCompleteRuleVO,
} from '../api/types/nodeAutoCompleteRule';
import { useApolloMqConfig } from '../hooks/useApolloMqConfig';
import { useListViewQuery } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';
import type {
  AutoCompleteTriggerType,
  NodeAutoCompleteRule,
  NodeAutoCompleteRuleForm,
} from '../types/nodeAutoCompleteRule';
import {
  createEmptyAutoCompleteRuleForm,
} from '../types/nodeAutoCompleteRule';
import NodeAutoCompletePrepareScriptHelpModal from './NodeAutoCompletePrepareScriptHelpModal';
import NodeAutoCompleteDecisionScriptHelpModal from './NodeAutoCompleteDecisionScriptHelpModal';
import NodeAutoCompleteRuleTestModal from './NodeAutoCompleteRuleTestModal';
import NodeAutoCompleteRuleRunLogModal from './NodeAutoCompleteRuleRunLogModal';
import NodeAutoCompleteRuleVersionModal from './NodeAutoCompleteRuleVersionModal';
import NodeAutoCompleteRuleTriggerModal from './NodeAutoCompleteRuleTriggerModal';
import NodeAutoCompleteScriptFullscreenEditor, { type ScriptEditorStep } from './NodeAutoCompleteScriptFullscreenEditor';
import { formatServerDateTime } from '../utils/formatServerDateTime';
import './NodeAutoCompleteRulesTab.css';

const { TextArea } = Input;

const TRIGGER_TYPE_OPTIONS: { value: AutoCompleteTriggerType; label: string }[] = [
  { value: 'api', label: '接口触发' },
  { value: 'mq', label: 'MQ触发' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function formSnapshotKey(form: NodeAutoCompleteRuleForm): string {
  return JSON.stringify({
    id: form.id ?? '',
    name: form.name,
    enabled: form.enabled,
    triggerType: form.triggerType,
    triggerKey: form.triggerKey ?? '',
    mqVhost: form.mqVhost ?? '',
    mqQueue: form.mqQueue ?? '',
    prepareScript: form.prepareScript,
    prepareTimeoutSec: form.prepareTimeoutSec,
    prepareRetryCount: form.prepareRetryCount,
    decisionScript: form.decisionScript,
    decisionTimeoutSec: form.decisionTimeoutSec,
    decisionRetryCount: form.decisionRetryCount,
  });
}

type Props = {
  canMutate: boolean;
  /** 规则变更后通知父级刷新下拉选项 */
  onRulesChanged?: () => void;
};

function triggerTypeLabel(type: AutoCompleteTriggerType): string {
  return TRIGGER_TYPE_OPTIONS.find(o => o.value === type)?.label ?? type;
}

function renderLastRun(rule: NodeAutoCompleteRule) {
  const run = rule.lastRun;
  const timeText = formatServerDateTime(run?.time);
  if (!run || run.status === 'none' || !timeText) {
    return (
      <span className="acr-last-run acr-last-run--none">
        <span className="acr-last-run__dot" aria-hidden />
        暂无执行
      </span>
    );
  }
  if (run.status === 'failed') {
    return (
      <span className="acr-last-run acr-last-run--failed" title={timeText}>
        <span className="acr-last-run__dot" aria-hidden />
        <span className="acr-last-run__detail">{timeText}</span>
      </span>
    );
  }
  return (
    <span className="acr-last-run acr-last-run--success" title={timeText}>
      <span className="acr-last-run__dot" aria-hidden />
      <span className="acr-last-run__detail">{timeText}</span>
    </span>
  );
}

function renderTriggerConfig(rule: NodeAutoCompleteRule) {
  const meta =
    rule.triggerType === 'mq'
      ? [
          rule.mqVhost ? `VHost: ${rule.mqVhost}` : null,
          rule.mqQueue ? `队列: ${rule.mqQueue}` : null,
        ].filter(Boolean).join(' · ') || '未配置 MQ'
      : (rule.triggerKey ? `Key: ${rule.triggerKey}` : '未配置触发 Key');
  return (
    <div className="acr-flow-cell">
      <div className="acr-flow-cell__tags">
        <IpcTag variant="primary">{triggerTypeLabel(rule.triggerType)}</IpcTag>
      </div>
      <div className="acr-flow-cell__meta" title={meta}>{meta}</div>
    </div>
  );
}

const NodeAutoCompleteRulesTab: FC<Props> = ({ canMutate, onRulesChanged }) => {
  const { vhostOptions, queueOptions } = useApolloMqConfig();
  const {
    draft: ruleDraft,
    patchDraft: patchRuleDraft,
    applied: ruleApplied,
    applyIfChanged: applyRuleIfChanged,
    reset: resetRuleQuery,
    patchApplied: patchRuleApplied,
    page,
    pageSize,
    setPagination: setRulePagination,
  } = useListViewQuery<{ name: string }>({
    listViewKey: LIST_VIEW_KEYS.autoCompleteRules,
    defaultFilters: { name: '' },
    emptyFilters: { name: '' },
  });
  const filterName = ruleDraft.name;
  const appliedFilterName = ruleApplied.name;
  const [rules, setRules] = useState<NodeAutoCompleteRule[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [logRule, setLogRule] = useState<NodeAutoCompleteRule | null>(null);
  const [versionRule, setVersionRule] = useState<NodeAutoCompleteRule | null>(null);
  const [prepareHelpOpen, setPrepareHelpOpen] = useState(false);
  const [decisionHelpOpen, setDecisionHelpOpen] = useState(false);
  const [fullscreenStep, setFullscreenStep] = useState<ScriptEditorStep | null>(null);
  const [form, setForm] = useState<NodeAutoCompleteRuleForm>(createEmptyAutoCompleteRuleForm());
  const [savedFormSnapshot, setSavedFormSnapshot] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isFormDirty = useMemo(() => {
    if (!form.id || savedFormSnapshot == null) return true;
    return formSnapshotKey(form) !== savedFormSnapshot;
  }, [form, savedFormSnapshot]);

  const canTestSavedRule = Boolean(form.id) && !isFormDirty;
  const testDisabledReason = !form.id
    ? '请先保存规则后再测试'
    : isFormDirty
      ? '有未保存的更改，请先保存'
      : undefined;

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nodeAutoCompleteRuleApi.getList({
        name: appliedFilterName.trim() || undefined,
        pageNum: page,
        pageSize,
      });
      setRules((res.list ?? []).map(mapNodeAutoCompleteRuleVO));
      setTotal(res.total ?? 0);
    } catch (e) {
      console.error('加载自动完成规则失败', e);
      message.error('加载自动完成规则失败');
      setRules([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilterName, page, pageSize]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const notifyRulesChanged = useCallback(() => {
    onRulesChanged?.();
    loadRules();
  }, [loadRules, onRulesChanged]);

  const openCreate = () => {
    if (!canMutate) return;
    const empty = createEmptyAutoCompleteRuleForm();
    setForm(empty);
    setSavedFormSnapshot(null);
    setShowModal(true);
  };

  const openEdit = (rule: NodeAutoCompleteRule) => {
    if (!canMutate) return;
    const next: NodeAutoCompleteRuleForm = {
      id: rule.id,
      name: rule.name,
      enabled: rule.enabled,
      triggerType: rule.triggerType,
      triggerKey: rule.triggerKey ?? '',
      mqVhost: rule.mqVhost,
      mqQueue: rule.mqQueue,
      prepareScript: rule.prepareScript,
      prepareTimeoutSec: rule.prepareTimeoutSec,
      prepareRetryCount: rule.prepareRetryCount,
      decisionScript: rule.decisionScript,
      decisionTimeoutSec: rule.decisionTimeoutSec,
      decisionRetryCount: rule.decisionRetryCount,
      version: rule.version ?? 1,
    };
    setForm(next);
    setSavedFormSnapshot(formSnapshotKey(next));
    setShowModal(true);
  };

  const openDuplicate = async (rule: NodeAutoCompleteRule) => {
    if (!canMutate) return;
    try {
      const detail = await nodeAutoCompleteRuleApi.getById(Number(rule.id));
      const mapped = mapNodeAutoCompleteRuleVO(detail);
      const copyName = `${mapped.name}(复制)`.slice(0, 100);
      const next: NodeAutoCompleteRuleForm = {
        name: copyName,
        enabled: mapped.enabled,
        triggerType: mapped.triggerType,
        triggerKey: mapped.triggerKey ?? '',
        mqVhost: mapped.mqVhost,
        mqQueue: mapped.mqQueue,
        prepareScript: mapped.prepareScript,
        prepareTimeoutSec: mapped.prepareTimeoutSec,
        prepareRetryCount: mapped.prepareRetryCount,
        decisionScript: mapped.decisionScript,
        decisionTimeoutSec: mapped.decisionTimeoutSec,
        decisionRetryCount: mapped.decisionRetryCount,
      };
      setForm(next);
      setSavedFormSnapshot(null);
      setShowModal(true);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '复制失败，无法加载原规则');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(createEmptyAutoCompleteRuleForm());
    setSavedFormSnapshot(null);
  };

  const validateForm = (candidate: NodeAutoCompleteRuleForm = form): boolean => {
    if (!candidate.name.trim()) {
      message.warning('请填写规则名称');
      return false;
    }
    if (candidate.triggerType === 'api' && !candidate.triggerKey?.trim()) {
      message.warning('请填写触发 Key');
      return false;
    }
    if (candidate.triggerType === 'mq') {
      if (!candidate.mqVhost) {
        message.warning('请选择 VHost');
        return false;
      }
      if (!candidate.mqQueue) {
        message.warning('请选择队列名称');
        return false;
      }
    }
    if (!candidate.prepareScript.trim()) {
      message.warning('请填写数据准备脚本');
      return false;
    }
    if (!candidate.decisionScript.trim()) {
      message.warning('请填写动作决策脚本');
      return false;
    }
    return true;
  };

  const persistForm = async (candidate: NodeAutoCompleteRuleForm) => {
    const body = buildNodeAutoCompleteRuleSaveBody(candidate);
    if (candidate.id) {
      const updated = await nodeAutoCompleteRuleApi.update(Number(candidate.id), { ...body, id: Number(candidate.id) });
      const next: NodeAutoCompleteRuleForm = {
        ...candidate,
        version: updated.version ?? candidate.version ?? 1,
      };
      setForm(next);
      setSavedFormSnapshot(formSnapshotKey(next));
      return next;
    }
    const created = await nodeAutoCompleteRuleApi.create(body);
    const next: NodeAutoCompleteRuleForm = {
      ...candidate,
      id: String(created.id),
      version: created.version ?? 1,
    };
    setForm(next);
    setSavedFormSnapshot(formSnapshotKey(next));
    return next;
  };

  const handleSaveScriptFromFullscreen = async (step: ScriptEditorStep, script: string) => {
    if (!canMutate) return;
    const field = step === 'prepare' ? 'prepareScript' : 'decisionScript';
    const candidate = { ...form, [field]: script };
    if (!validateForm(candidate)) {
      throw new Error('请先完善规则基本信息后再保存脚本');
    }
    await persistForm(candidate);
    notifyRulesChanged();
  };

  const handleSave = async () => {
    if (!canMutate) return;
    if (!validateForm()) return;
    setSaving(true);
    try {
      const hadId = Boolean(form.id);
      await persistForm(form);
      message.success(hadId ? '保存成功' : '创建成功');
      notifyRulesChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (rule: NodeAutoCompleteRule) => {
    if (!canMutate) return;
    if (rule.linkedNodeCount) {
      message.warning(`当前有 ${rule.linkedNodeCount} 个节点正在使用该规则，无法删除`);
      return;
    }
    ipcConfirm({
      title: '确认删除该规则？',
      content: '删除后不可恢复。',
      okText: '删除',
      onOk: async () => {
        try {
          await nodeAutoCompleteRuleApi.delete(Number(rule.id));
          message.success('删除成功');
          notifyRulesChanged();
        } catch (e) {
          message.error(e instanceof Error ? e.message : '删除失败');
        }
      },
    });
  };

  const handleToggleEnabled = async (rule: NodeAutoCompleteRule, enabled: boolean) => {
    if (!canMutate) return;
    try {
      await nodeAutoCompleteRuleApi.setEnabled(Number(rule.id), enabled);
      message.success(enabled ? '已启用' : '已停用');
      notifyRulesChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '状态更新失败');
    }
  };

  const openTest = (rule?: NodeAutoCompleteRule) => {
    if (rule) {
      const next: NodeAutoCompleteRuleForm = {
        id: rule.id,
        name: rule.name,
        enabled: rule.enabled,
        triggerType: rule.triggerType,
        triggerKey: rule.triggerKey ?? '',
        mqVhost: rule.mqVhost,
        mqQueue: rule.mqQueue,
        prepareScript: rule.prepareScript,
        prepareTimeoutSec: rule.prepareTimeoutSec,
        prepareRetryCount: rule.prepareRetryCount,
        decisionScript: rule.decisionScript,
        decisionTimeoutSec: rule.decisionTimeoutSec,
        decisionRetryCount: rule.decisionRetryCount,
        version: rule.version ?? 1,
      };
      setForm(next);
      setSavedFormSnapshot(formSnapshotKey(next));
      setShowTestModal(true);
      return;
    }
    if (!canTestSavedRule) {
      message.warning(testDisabledReason ?? '请先保存规则后再测试');
      return;
    }
    if (!validateForm()) return;
    setShowTestModal(true);
  };

  const handleTest = () => {
    if (!canTestSavedRule) {
      message.warning(testDisabledReason ?? '请先保存规则后再测试');
      return;
    }
    if (!validateForm()) return;
    setShowTestModal(true);
  };

  const formatScript = (field: 'prepareScript' | 'decisionScript') => {
    const raw = form[field];
    try {
      // eslint-disable-next-line no-new-func
      new Function(raw);
      message.success('语法校验通过');
    } catch {
      message.warning('脚本语法可能存在问题，请检查');
    }
  };

  const columns = useMemo<ColumnsType<NodeAutoCompleteRule>>(() => {
    const cols: ColumnsType<NodeAutoCompleteRule> = [
      {
        title: '规则名称',
        dataIndex: 'name',
        width: 200,
        render: value => <span className="acr-rule-name">{value || '—'}</span>,
      },
      {
        title: '版本',
        key: 'version',
        width: 80,
        render: (_, rule) => rule.version ?? 1,
      },
      {
        title: '关联节点',
        key: 'linkedNodes',
        width: 100,
        align: 'center',
        render: (_, rule) => {
          const count = rule.linkedNodeCount ?? 0;
          const names = rule.linkedNodeNames ?? [];
          if (count <= 0) {
            return <span className="acr-linked-nodes acr-linked-nodes--muted">0</span>;
          }
          return (
            <Tooltip
              title={(
                <ul className="acr-linked-nodes-tooltip">
                  {names.map((name, idx) => (
                    <li key={`${name}-${idx}`}>{name}</li>
                  ))}
                </ul>
              )}
            >
              <span className="acr-linked-nodes acr-linked-nodes--count">{count}</span>
            </Tooltip>
          );
        },
      },
      {
        title: '触发方式',
        key: 'trigger',
        width: 240,
        render: (_, rule) => renderTriggerConfig(rule),
      },
      {
        title: '最近执行',
        key: 'lastRun',
        width: 200,
        render: (_, rule) => renderLastRun(rule),
      },
      {
        title: '状态',
        key: 'enabled',
        width: 100,
        align: 'center',
        render: (_, rule) => (
          <IpcSwitch
            size="small"
            checked={rule.enabled}
            disabled={!canMutate}
            onChange={checked => handleToggleEnabled(rule, checked)}
          />
        ),
      },
      {
        title: '创建时间',
        key: 'createTime',
        width: 170,
        render: (_, rule) => formatServerDateTime(rule.createTime),
      },
      {
        title: '更新时间',
        key: 'updateTime',
        width: 170,
        render: (_, rule) => formatServerDateTime(rule.updateTime),
      },
    ];

    if (canMutate) {
      cols.push({
        title: '操作',
        key: 'actions',
        width: 160,
        fixed: 'right',
        render: (_, rule) => {
          const moreItems: MenuProps['items'] = [
            { key: 'test', label: '测试', onClick: () => openTest(rule) },
            { key: 'versions', label: '版本', onClick: () => setVersionRule(rule) },
            { key: 'duplicate', label: '复制', onClick: () => void openDuplicate(rule) },
            { key: 'delete', label: '删除', danger: true, onClick: () => handleDelete(rule) },
          ];
          return (
            <div className="acr-row-actions">
              <IpcRowActions
                actions={[
                  { key: 'edit', label: '编辑', onClick: () => openEdit(rule) },
                  { key: 'log', label: '日志', onClick: () => setLogRule(rule) },
                ]}
              />
              <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                <button type="button" className="acr-more-btn" aria-label="更多操作">
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          );
        },
      });
    }

    return cols;
  }, [canMutate]);

  const mqEmptyHint = '暂无 Apollo MQ 配置，请等待配置接入';

  return (
    <div className="config-content template-config-content node-auto-complete-rules-tab">
      <div className="ipc-table-viewport milestone-node-viewport">
        <div className="ipc-table-viewport__top">
          <IpcFilterToolbar
            className="milestone-filter-toolbar"
            onSubmit={applyRuleIfChanged}
            onReset={resetRuleQuery}
            chipItems={
              appliedFilterName.trim()
                ? [{ key: 'name', label: `规则名称: ${appliedFilterName.trim()}` }]
                : []
            }
            onRemoveChip={() => {
              patchRuleApplied({ name: '' });
            }}
            onClearChips={resetRuleQuery}
            extra={(
              <div className="acr-toolbar-actions">
                <IpcButton icon={<ThunderboltOutlined />} onClick={() => setShowTriggerModal(true)}>
                  触发
                </IpcButton>
                {canMutate ? (
                  <IpcButton variant="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    新增规则
                  </IpcButton>
                ) : null}
              </div>
            )}
          >
            <Input
              className="ipc-filter-bar__search"
              placeholder="搜索规则名称"
              value={filterName}
              onChange={e => patchRuleDraft({ name: e.target.value })}
              onPressEnter={() => applyRuleIfChanged()}
              allowClear
            />
          </IpcFilterToolbar>
        </div>

        <div className="ipc-table-viewport__table milestone-template-table-area">
          {rules.length > 0 || loading ? (
            <IpcTable<NodeAutoCompleteRule>
              listViewKey={LIST_VIEW_KEYS.autoCompleteRules}
              compact
              frameless
              resizable
              loading={loading}
              rowKey={rule => rule.id}
              columns={columns}
              dataSource={rules}
              pagination={{
                current: page,
                pageSize,
                total,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: (p, ps) => {
                  setRulePagination(p, ps);
                },
              }}
              size="small"
              tableLayout="fixed"
              autoHeight
              autoHeightDeps={[rules.length, page, pageSize, canMutate, loading]}
              scroll={{ x: canMutate ? 1420 : 1260 }}
              panelClassName="milestone-config-table-panel"
              scrollClassName="milestone-config-table-wrap"
              className="milestone-config-table acr-rules-table"
            />
          ) : (
            <IpcEmpty
              title={canMutate ? '暂无自动完成规则，请点击「新增规则」' : '暂无自动完成规则'}
              description="创建规则后，可在节点配置中关联使用"
            />
          )}
        </div>
      </div>

      <IpcModal
        title={null}
        open={showModal && canMutate && fullscreenStep == null}
        onCancel={closeModal}
        width={960}
        className="acr-rule-modal"
        footer={(
          <div className="acr-rule-modal__footer">
            <span title={testDisabledReason}>
              <IpcButton
                variant="secondary"
                icon={<PlayCircleOutlined />}
                disabled={!canTestSavedRule}
                onClick={handleTest}
              >
                测试
              </IpcButton>
            </span>
            <div className="acr-rule-modal__footer-right">
              <IpcButton variant="secondary" onClick={closeModal}>取消</IpcButton>
              <IpcButton variant="primary" onClick={handleSave} loading={saving}>保存</IpcButton>
            </div>
          </div>
        )}
      >
        <div className="acr-rule-modal__body">
          <div className="acr-rule-modal__name-row">
            <Input
              className="acr-rule-modal__name"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="请输入规则名称"
              maxLength={100}
            />
            {form.id ? (
              <span className="acr-rule-modal__version">当前版本 {form.version ?? 1}</span>
            ) : null}
          </div>
          <section className="acr-rule-section">
            <div className="acr-rule-section__head">
              <span className="acr-rule-section__step">1</span>
              <span className="acr-rule-section__title">触发设置</span>
            </div>
            <div className="acr-rule-section__body acr-trigger-fields">
              <IpcFormField label="触发方式" required>
                <Select
                  value={form.triggerType}
                  onChange={(v: AutoCompleteTriggerType) => setForm(prev => ({
                    ...prev,
                    triggerType: v,
                    triggerKey: v === 'api' ? prev.triggerKey : '',
                    mqVhost: v === 'mq' ? prev.mqVhost : undefined,
                    mqQueue: v === 'mq' ? prev.mqQueue : undefined,
                  }))}
                  options={TRIGGER_TYPE_OPTIONS}
                  style={{ width: '100%' }}
                  getPopupContainer={() => document.body}
                />
              </IpcFormField>

              {form.triggerType === 'api' ? (
                <IpcFormField label="触发Key" required>
                  <Input
                    value={form.triggerKey ?? ''}
                    onChange={e => setForm(prev => ({ ...prev, triggerKey: e.target.value }))}
                    placeholder="多个 Key 用逗号分隔；填 * 表示任意 Key 均触发"
                    maxLength={200}
                  />
                </IpcFormField>
              ) : (
                <>
                  <IpcFormField label="VHost" required>
                    <Select
                      value={form.mqVhost}
                      onChange={v => setForm(prev => ({ ...prev, mqVhost: v, mqQueue: undefined }))}
                      options={vhostOptions}
                      placeholder="请选择 VHost"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      notFoundContent={mqEmptyHint}
                      style={{ width: '100%' }}
                      getPopupContainer={() => document.body}
                    />
                  </IpcFormField>
                  <IpcFormField label="队列名称" required>
                    <Select
                      value={form.mqQueue}
                      onChange={v => setForm(prev => ({ ...prev, mqQueue: v }))}
                      options={queueOptions}
                      placeholder="请选择队列"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      notFoundContent={mqEmptyHint}
                      disabled={!form.mqVhost}
                      style={{ width: '100%' }}
                      getPopupContainer={() => document.body}
                    />
                  </IpcFormField>
                </>
              )}
            </div>
          </section>

          <div className="acr-rule-scripts">
            <section className="acr-rule-section acr-rule-section--script">
              <div className="acr-rule-section__head">
                <span className="acr-rule-section__step">2</span>
                <span className="acr-rule-section__title">数据准备</span>
                <span className="acr-rule-section__required">*</span>
                <div className="acr-rule-section__head-actions">
                  <button
                    type="button"
                    className="acr-script-fullscreen-btn"
                    aria-label="全屏编辑数据准备脚本"
                    onClick={() => setFullscreenStep('prepare')}
                  >
                    <FullscreenOutlined />
                  </button>
                  <button
                    type="button"
                    className="acr-script-help-btn"
                    aria-label="查看数据准备脚本说明"
                    onClick={() => setPrepareHelpOpen(true)}
                  >
                    <QuestionCircleOutlined />
                  </button>
                </div>
              </div>
              <div className="acr-script-editor">
                <div className="acr-script-editor__toolbar">
                  <span className="acr-script-editor__lang">数据准备 · JAVASCRIPT</span>
                  <div className="acr-script-editor__actions">
                    <button type="button" className="acr-script-editor__link" onClick={() => formatScript('prepareScript')}>
                      校验语法
                    </button>
                  </div>
                </div>
                <TextArea
                  className="acr-script-editor__code"
                  value={form.prepareScript}
                  onChange={e => setForm(prev => ({ ...prev, prepareScript: e.target.value }))}
                  rows={12}
                  spellCheck={false}
                />
              </div>
              <div className="acr-script-meta">
                <IpcFormField label="超时时间 (秒)">
                  <InputNumber
                    min={1}
                    max={3600}
                    value={form.prepareTimeoutSec}
                    onChange={v => setForm(prev => ({ ...prev, prepareTimeoutSec: typeof v === 'number' ? v : 100 }))}
                    style={{ width: '100%' }}
                  />
                </IpcFormField>
                <IpcFormField label="重试次数">
                  <InputNumber
                    min={0}
                    max={10}
                    value={form.prepareRetryCount}
                    onChange={v => setForm(prev => ({ ...prev, prepareRetryCount: typeof v === 'number' ? v : 0 }))}
                    style={{ width: '100%' }}
                  />
                </IpcFormField>
              </div>
            </section>

            <section className="acr-rule-section acr-rule-section--script">
              <div className="acr-rule-section__head">
                <span className="acr-rule-section__step">3</span>
                <span className="acr-rule-section__title">动作决策</span>
                <span className="acr-rule-section__required">*</span>
                <div className="acr-rule-section__head-actions">
                  <button
                    type="button"
                    className="acr-script-fullscreen-btn"
                    aria-label="全屏编辑动作决策脚本"
                    onClick={() => setFullscreenStep('decision')}
                  >
                    <FullscreenOutlined />
                  </button>
                  <button
                    type="button"
                    className="acr-script-help-btn"
                    aria-label="查看动作决策脚本说明"
                    onClick={() => setDecisionHelpOpen(true)}
                  >
                    <QuestionCircleOutlined />
                  </button>
                </div>
              </div>
              <div className="acr-script-editor">
                <div className="acr-script-editor__toolbar">
                  <span className="acr-script-editor__lang">动作决策 · JAVASCRIPT</span>
                  <div className="acr-script-editor__actions">
                    <button type="button" className="acr-script-editor__link" onClick={() => formatScript('decisionScript')}>
                      校验语法
                    </button>
                  </div>
                </div>
                <TextArea
                  className="acr-script-editor__code"
                  value={form.decisionScript}
                  onChange={e => setForm(prev => ({ ...prev, decisionScript: e.target.value }))}
                  rows={12}
                  spellCheck={false}
                />
              </div>
              <div className="acr-script-meta">
                <IpcFormField label="超时时间 (秒)">
                  <InputNumber
                    min={1}
                    max={3600}
                    value={form.decisionTimeoutSec}
                    onChange={v => setForm(prev => ({ ...prev, decisionTimeoutSec: typeof v === 'number' ? v : 100 }))}
                    style={{ width: '100%' }}
                  />
                </IpcFormField>
                <IpcFormField label="重试次数">
                  <InputNumber
                    min={0}
                    max={10}
                    value={form.decisionRetryCount}
                    onChange={v => setForm(prev => ({ ...prev, decisionRetryCount: typeof v === 'number' ? v : 0 }))}
                    style={{ width: '100%' }}
                  />
                </IpcFormField>
              </div>
            </section>
          </div>

          <div className="acr-rule-modal__status-row">
            <span className="acr-rule-modal__status-label">启用规则</span>
            <IpcSwitch
              checked={form.enabled}
              onChange={checked => setForm(prev => ({ ...prev, enabled: checked }))}
            />
            {form.enabled ? (
              <IpcStatusBadge variant="success">已启用</IpcStatusBadge>
            ) : (
              <IpcStatusBadge variant="muted">已停用</IpcStatusBadge>
            )}
          </div>
        </div>
      </IpcModal>

      <NodeAutoCompletePrepareScriptHelpModal
        open={prepareHelpOpen}
        onClose={() => setPrepareHelpOpen(false)}
      />

      <NodeAutoCompleteDecisionScriptHelpModal
        open={decisionHelpOpen}
        onClose={() => setDecisionHelpOpen(false)}
      />

      <NodeAutoCompleteRuleTestModal
        open={showTestModal}
        onClose={() => setShowTestModal(false)}
        form={form}
      />

      <NodeAutoCompleteRuleTriggerModal
        open={showTriggerModal}
        onClose={() => setShowTriggerModal(false)}
        rules={rules}
      />

      <NodeAutoCompleteRuleRunLogModal
        open={Boolean(logRule)}
        onClose={() => setLogRule(null)}
        ruleId={logRule ? Number(logRule.id) : 0}
        ruleName={logRule?.name ?? ''}
      />

      <NodeAutoCompleteRuleVersionModal
        open={Boolean(versionRule)}
        onClose={() => setVersionRule(null)}
        ruleId={versionRule ? Number(versionRule.id) : 0}
        ruleName={versionRule?.name ?? ''}
      />

      <NodeAutoCompleteScriptFullscreenEditor
        open={fullscreenStep != null}
        step={fullscreenStep ?? 'prepare'}
        script={fullscreenStep === 'decision' ? form.decisionScript : form.prepareScript}
        timeoutSec={fullscreenStep === 'decision' ? form.decisionTimeoutSec : form.prepareTimeoutSec}
        onClose={() => setFullscreenStep(null)}
        onSaved={script => {
          if (fullscreenStep === 'prepare') {
            setForm(prev => ({ ...prev, prepareScript: script }));
            return;
          }
          if (fullscreenStep === 'decision') {
            setForm(prev => ({ ...prev, decisionScript: script }));
          }
        }}
        onSave={script => {
          if (!fullscreenStep) return Promise.resolve();
          return handleSaveScriptFromFullscreen(fullscreenStep, script);
        }}
      />
    </div>
  );
};

export default NodeAutoCompleteRulesTab;
